import { Component, EventEmitter, inject, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { EmployeeConsultantsService } from '../../../../../services/api/hr/employee-consultants.service';
import { AlertInfo } from '../../../../../models/alert-info';
import { SavedDataInfo } from '../../../../../models/form/saved-data-info';
import { InputFieldComponent } from '../../../../../components/form/input-field/input-field.component';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { AutocompleteFieldComponent } from "src/app/components/form/autocomplete-field/autocomplete-field.component";
import { HttpParams } from '@angular/common/http';
import { CustomerService } from 'src/app/services/api/customer.service';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';

const params = new HttpParams().set('size', '9999');


@Component({
  selector: 'employee-consultants-previous-jobs-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbAlert,
    InputFieldComponent,
    AutocompleteFieldComponent,
    AutocompleteFieldComponent
  ],
  templateUrl: './employee-consultants-previous-jobs-form.component.html',
  styleUrl: './employee-consultants-previous-jobs-form.component.scss'
})
export class EmployeeConsultantsPreviousJobsFormComponent implements OnInit {
  @Input() employeeId: number | undefined;
  @Input() employee: any;
  @Input() employeeWorkCustomerList: any[] = [];
  @Input() readonly: boolean = false;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();

  @ViewChildren(AutocompleteFieldComponent) autocompleteFields!: QueryList<AutocompleteFieldComponent>;

  protected readonly customerService = inject(CustomerService);


  form!: FormGroup;
  alertInfo: AlertInfo = { show: false, type: 'success', message: '' };
  loading: boolean = false;
  validationErrors: any[] = [];
  jobs: any[] = [];
  currentEditingJob: any = null;
  isEditing: boolean = false;
  jobToDelete: any = null;
  customerOptions: Array<{ key: number; value: string }> = [];
  selectedCustomerId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private employeeConsultantsService: EmployeeConsultantsService,
    private confirmationDialogService: ConfirmationDialogService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    setTimeout(() => {
      if (this.employeeWorkCustomerList?.length > 0) {
        this.jobs = [...this.employeeWorkCustomerList];
        this.employeeWorkCustomerList.forEach(job => {
          if (job.customer?.customerId && job.customer?.companyName) {
            const exists = this.customerOptions.some(opt => opt.key === job.customer.customerId);
            if (!exists) {
              this.customerOptions.push({
                key: job.customer.customerId,
                value: job.customer.companyName
              });
            }
          }
        });
      }
    }, 1000);
  }

  private initForm(): void {
    this.form = this.fb.group({
      engageTime: ['', Validators.required],
      terminationTime: ['', Validators.required],
      companyName: ['', Validators.required],
      role: ['', Validators.required]
    });

    if (this.readonly) {
      this.form.disable();
    }
  }

  resetForm(): void {
    this.form.reset();
    this.currentEditingJob = null;
    this.isEditing = false;
    this.selectedCustomerId = null;
  }

  showAlertInfo(type: string, message: string): void {
    this.alertInfo = { show: true, type, message };
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }

  updateValidationErrors(errors: any[]): void {
    this.validationErrors = errors;
    errors.forEach(error => {
      const control = this.form.get(error.field);
      if (control) {
        control.setErrors({ serverError: error.message });
      }
    });
  }

  editJob(job: any): void {
    this.currentEditingJob = job;
    this.isEditing = true;
    this.selectedCustomerId = job.customer?.customerId || null;
    this.form.patchValue({
      engageTime: job.engageTime,
      terminationTime: job.terminationTime,
      companyName: job.customer?.companyName || '',
      role: job.role
    });
  }

  deleteJob(job: any): void {
    this.jobToDelete = job;
    this.confirmationDialogService.open(
      'Are you sure you want to delete this job?',
      () => this.confirmDelete()
    );
  }

  confirmDelete(): void {
    if (this.jobToDelete) {
      if (this.employeeId && this.jobToDelete.employeeJobId) {
        this.loading = true;
        this.employeeConsultantsService.deleteEmployeeJob(this.employeeId, this.jobToDelete.employeeJobId).subscribe({
          next: () => {
            this.jobs = this.jobs.filter(j => j.employeeJobId !== this.jobToDelete.employeeJobId);
            this.showAlertInfo('success', 'Job deleted successfully');
            this.loading = false;
            this.onSavedData.emit({ success: true });
          },
          error: (error) => {
            this.loading = false;
            this.showAlertInfo('danger', error.error?.message || 'Error deleting job');
          },
          complete: () => {
            this.jobToDelete = null;
          }
        });
      } else {
        this.jobs = this.jobs.filter(j => j.employeeJobId !== this.jobToDelete.employeeJobId);
        this.showAlertInfo('success', 'Job deleted successfully');
        this.jobToDelete = null;
      }
    }
  }

  getCustomerName(customerId: number): string {
    const job = this.employeeWorkCustomerList?.find(j => j.customer?.customerId === customerId);
    if (job?.customer?.companyName) {
      return job.customer.companyName;
    }
    const option = this.customerOptions.find(opt => opt.key === customerId);
    if (option) {
      return option.value;
    }
    
    const autocompleteField = this.autocompleteFields?.first;
    if (autocompleteField?.filteredOptions) {      
      const fieldOption = autocompleteField.filteredOptions.find(opt => Number(opt.key) === customerId);
      if (fieldOption) {
        return fieldOption.value;
      }
    }
    
    return 'Unknown';
  }

  onSubmit(): void {
    if (this.loading) return;

    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        control?.markAsTouched();
      });
      this.showAlertInfo('danger', 'Please fill in all required fields correctly.');
      return;
    }

    this.loading = true;
    const jobData = { 
      ...this.form.value,
      customerId: this.selectedCustomerId
    };
    delete jobData.companyName; 

    if (this.employeeId) {
      if (this.isEditing && this.currentEditingJob) {
        this.employeeConsultantsService.updateEmployeeJob(
          this.employeeId,
          this.currentEditingJob.employeeJobId,
          jobData
        ).subscribe({
          next: (response) => {
            if (response?.data?.employeeJob) {
              const jobIndex = this.jobs.findIndex(job => job.employeeJobId === this.currentEditingJob.employeeJobId);
              if (jobIndex !== -1) {
                this.jobs[jobIndex] = response.data.employeeJob;
              }
              this.resetForm();
              this.showAlertInfo('success', 'Job updated successfully');
              this.onSavedData.emit({ success: true, response });
            }
          },
          error: (error) => {
            this.showAlertInfo('danger', error.error?.message || 'Error updating job');
            if (error.error?.errors) {
              this.updateValidationErrors(error.error.errors);
            }
          },
          complete: () => {
            this.loading = false;
          }
        });
      } else {
        this.employeeConsultantsService.createEmployeeJob(
          this.employeeId,
          jobData
        ).subscribe({
          next: (response) => {
            if (response?.data?.employeeJob) {
              this.jobs.push(response.data.employeeJob);
              this.resetForm();
              this.showAlertInfo('success', 'Job added successfully');
              this.onSavedData.emit({ success: true, response });
            }
          },
          error: (error) => {
            this.showAlertInfo('danger', error.error?.message || 'Error creating job');
            if (error.error?.errors) {
              this.updateValidationErrors(error.error.errors);
            }
          },
          complete: () => {
            this.loading = false;
          }
        });
      }
    } else {
      if (this.isEditing && this.currentEditingJob) {
        const jobIndex = this.jobs.findIndex(job => job.employeeJobId === this.currentEditingJob.employeeJobId);
        if (jobIndex !== -1) {
          this.jobs[jobIndex] = { ...this.currentEditingJob, ...jobData };
          this.resetForm();
          this.showAlertInfo('success', 'Job updated successfully');
          this.onSavedData.emit({ success: true });
        }
      } else {
        const newJob = {
          employeeJobId: Date.now(),
          ...jobData,
          customer: {
            customerId: this.selectedCustomerId,
            companyName: this.getCustomerName(this.selectedCustomerId!)
          }
        };

        this.jobs.push(newJob);
        this.resetForm();
        this.showAlertInfo('success', 'Job added successfully');
        this.onSavedData.emit({ success: true });
      }
      this.loading = false;
    }
  }

  getEngageTimeControl(): FormControl { return this.getFormControl('engageTime'); }
  getTerminationTimeControl(): FormControl { return this.getFormControl('terminationTime'); }
  getCompanyControl(): FormControl { return this.getFormControl('companyName'); }
  getRoleControl(): FormControl { return this.getFormControl('role'); }

  getFormControl(name: string): FormControl { return this.form.get(name) as FormControl; }

  getJobsData(): any[] {
    return this.jobs.map(job => ({
      employeeJobId: job.employeeJobId,
      engageTime: job.engageTime,
      terminationTime: job.terminationTime,
      role: job.role,
      customer: {
        companyName: job.customer?.companyName
      }
    }));
  }

  
   onSearchValueChange(value: any): void {
    this.selectedCustomerId = value;
    this.disableAutocompleteDropdowns();
    setTimeout(() => {
      this.enableAutocompleteDropdowns(500);
    }, 100);
  }

  onCompanyOptionsLoaded(options: Array<{ key: string | number; value: string }>): void {    
    this.customerOptions = options.map(opt => ({
      key: Number(opt.key),
      value: opt.value
    }));
  } 

    private disableAutocompleteDropdowns(): void {
    if (this.autocompleteFields) {
      this.autocompleteFields.forEach(field => {
        field.setPreventAutoOpen(true);
        field.isDropdownOpen = false;
      });
    }
  }

  private enableAutocompleteDropdowns(delay: number = 2000): void {
    setTimeout(() => {
      if (this.autocompleteFields) {
        this.autocompleteFields.forEach(field => {
          field.setPreventAutoOpen(false);
        });
      }
    }, delay);
  }

  validateForm(): boolean {
    return true;
  }
}
